import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../../supabaseClient';
import { Task, PhysicalUnit, Level, ProjectInfo, ProtocolState, WeeklyPlan, GraphicPlan, GraphicPlanRegion } from '../../types';
import { isMatrixValueApplies, isTaskUnitAssigned } from '../../utils/planningUtils';
import { PdfKonvaViewer, PdfRegion, PdfKonvaViewerHandle } from './PdfKonvaViewer';
import { formatPhysicalUnitLabel } from './GraphicProgressPlanningView';

export interface GraphicProgressTaskViewProps {
    activeProjectId: string;
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, any>;
    protocolConfigs: any;
    projectInfo: ProjectInfo;
    savedProtocols: Record<string, ProtocolState>;
    weeklyPlans: WeeklyPlan[];
    selectedTaskId?: string | null;
    activePlanId?: number | null;
    onClose?: () => void;
}

export const GraphicProgressTaskView: React.FC<GraphicProgressTaskViewProps> = ({
    activeProjectId,
    tasks,
    units,
    levels,
    matrixData,
    protocolConfigs,
    projectInfo,
    savedProtocols,
    weeklyPlans,
    selectedTaskId,
    activePlanId,
    onClose
}) => {
    const [plans, setPlans] = useState<GraphicPlan[]>([]);
    const [regions, setRegions] = useState<GraphicPlanRegion[]>([]);
    const [loading, setLoading] = useState(true);
    
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const konvaViewerRef = useRef<PdfKonvaViewerHandle>(null);

    // Fallbacks defensivos para props (prevención de nulos absolutos)
    const safeUnits = units || [];
    const safeLevels = levels || [];
    const safeTasks = tasks || [];
    const safeSavedProtocols = savedProtocols || {};
    const safeProtocolConfigs = protocolConfigs || {};
    const safeMatrixData = matrixData || {};

    const loadData = async () => {
        setLoading(true);
        try {
            const [plansRes, regionsRes] = await Promise.all([
                supabase.from('graphic_plans').select('*').eq('project_id', activeProjectId),
                supabase.from('graphic_plan_regions').select('*').eq('project_id', activeProjectId)
            ]);
            
            if (plansRes.data) {
                setPlans(plansRes.data);
                if (plansRes.data.length > 0) setSelectedPlanId(plansRes.data[0].id);
            } else {
                setPlans([]);
            }

            if (regionsRes.data) {
                setRegions(regionsRes.data);
            } else {
                setRegions([]);
            }
        } catch (err) {
            console.error("Error loading graphic progress data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!activeProjectId) return;
        loadData();
    }, [activeProjectId]);

    const activePlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);
    const activePlanRegions = useMemo(() => regions.filter(r => r.plan_id === selectedPlanId), [regions, selectedPlanId]);

    // Lógica de cálculo de estado
    const isProtocolProgressed = (taskId: string, unitId: string) => {
        return Object.values(safeSavedProtocols).some((p: ProtocolState) => {
            if (p.taskId === taskId && p.unitId === unitId) {
                const config = safeProtocolConfigs[taskId] || projectInfo?.protocolConfigs?.[taskId];
                const group = p.groupId
                    ? config?.reviewerGroups?.find((g: any) => g.id === p.groupId)
                    : config?.reviewerGroups?.find((g: any) => g.unitIds.includes(p.unitId));

                if (group) {
                    return group.reviewers.some((rev: any, idx: number) => rev.generatesProgress && p.signatures[idx]);
                }
            }
            return false;
        });
    };

    const calculateUnitProgressLocal = (taskId: string, unitId: string): number => {
        const config = safeProtocolConfigs[taskId] || projectInfo?.protocolConfigs?.[taskId];
        const appliesProtocol = config?.applies ?? false;

        if (appliesProtocol && isProtocolProgressed(taskId, unitId)) {
            return 100;
        }

        // Sum up % Cumplimiento in all closed weekly plans
        let totalCumplimiento = 0;
        if (weeklyPlans && Array.isArray(weeklyPlans)) {
            weeklyPlans.forEach(plan => {
                if (plan.status === 'Cerrada' && plan.allocations) {
                    plan.allocations.forEach(alloc => {
                        if (alloc.taskId === taskId && alloc.unitId === unitId) {
                            if (alloc.percentCumplimiento !== undefined) {
                                totalCumplimiento += alloc.percentCumplimiento;
                            } else if (alloc.executed) {
                                totalCumplimiento += 100;
                            }
                        }
                    });
                }
            });
        }

        return Math.min(totalCumplimiento, 100);
    };

    const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);

    // Filter tasks assigned to the selected unit in Matrix
    const unitTasksBreakdown = useMemo(() => {
        if (!selectedUnitId) return [];

        const assignedTasks: {
            task: Task;
            progress: number;
            isProgrammed: boolean;
            wasNotExecuted: boolean;
            status: 'completed' | 'programmed' | 'not_executed' | 'in_progress' | 'no_progress';
        }[] = [];

        const currentActivePlan = (activePlanId !== undefined && activePlanId !== null)
            ? weeklyPlans.find(p => p.id === activePlanId)
            : weeklyPlans.find(p => p.status !== 'Cerrada');

        const traverse = (list: Task[]) => {
            list.forEach(t => {
                if (t.type === 'task') {
                    const isAssigned = isTaskUnitAssigned(t.id, selectedUnitId, safeMatrixData, safeTasks);
                    if (isAssigned) {
                        const progress = calculateUnitProgressLocal(t.id, selectedUnitId);
                        const isProgrammed = currentActivePlan
                            ? currentActivePlan.allocations?.some(a => a.taskId === t.id && a.unitId === selectedUnitId)
                            : false;
                        const wasNotExecuted = weeklyPlans
                            .filter(p => p.status === 'Cerrada' && (!currentActivePlan || p.id !== currentActivePlan.id))
                            .some(p => p.allocations?.some(a => a.taskId === t.id && a.unitId === selectedUnitId && a.executed === false));

                        let status: 'completed' | 'programmed' | 'not_executed' | 'in_progress' | 'no_progress' = 'no_progress';
                        if (progress >= 100) {
                            status = 'completed';
                        } else if (isProgrammed) {
                            status = 'programmed';
                        } else if (wasNotExecuted) {
                            status = 'not_executed';
                        } else if (progress > 0) {
                            status = 'in_progress';
                        } else {
                            status = 'no_progress';
                        }

                        assignedTasks.push({
                            task: t,
                            progress,
                            isProgrammed: !!isProgrammed,
                            wasNotExecuted,
                            status
                        });
                    }
                }
                if (t.children && t.children.length > 0) {
                    traverse(t.children);
                }
            });
        };

        traverse(safeTasks);
        return assignedTasks;
    }, [selectedUnitId, safeMatrixData, safeTasks, weeklyPlans, activePlanId, safeProtocolConfigs, safeSavedProtocols]);

    // Mapeo a PdfRegion para Konva
    const konvaRegions: PdfRegion[] = useMemo(() => {
        const currentActivePlan = (activePlanId !== undefined && activePlanId !== null)
            ? weeklyPlans.find(p => p.id === activePlanId)
            : weeklyPlans.find(p => p.status !== 'Cerrada');

        return activePlanRegions.map(reg => {
            const unit = safeUnits.find(u => u?.id === reg.physical_unit_id);
            const level = safeLevels.find(l => l?.id === reg.level_id);
            const shortLabel = formatPhysicalUnitLabel(unit?.name, level?.name, reg.label || '');
            
            let color = 'rgba(203, 213, 225, 0.4)'; // slate-300 default (gris)
            
            if (reg.physical_unit_id) {
                if (selectedTaskId) {
                    // Specific task mode
                    const isAssigned = isTaskUnitAssigned(selectedTaskId, reg.physical_unit_id, safeMatrixData, safeTasks);
                    
                    if (isAssigned) {
                        const progress = calculateUnitProgressLocal(selectedTaskId, reg.physical_unit_id);
                        
                        const isProgrammed = currentActivePlan
                            ? currentActivePlan.allocations?.some(alloc => alloc.taskId === selectedTaskId && alloc.unitId === reg.physical_unit_id)
                            : false;
                            
                        const wasNotExecutedPrevWeek = weeklyPlans
                            .filter(p => p.status === 'Cerrada' && (!currentActivePlan || p.id !== currentActivePlan.id))
                            .some(p => p.allocations?.some(a => a.taskId === selectedTaskId && a.unitId === reg.physical_unit_id && a.executed === false));

                        if (progress >= 100) {
                            color = 'rgba(34, 197, 94, 0.65)'; // Completado (#22C55E)
                        } else if (isProgrammed) {
                            color = 'rgba(59, 130, 246, 0.75)'; // Programado (#3B82F6)
                        } else if (wasNotExecutedPrevWeek) {
                            color = 'rgba(239, 68, 68, 0.70)'; // No Ejecutado (#EF4444)
                        } else if (progress > 0) {
                            color = 'rgba(234, 179, 8, 0.70)'; // Avance Parcial (#EAB308)
                        } else {
                            color = 'rgba(156, 163, 175, 0.45)'; // Sin Avance (#9CA3AF)
                        }
                    }
                } else {
                    // Global unit overview mode
                    const assignedTasks = safeTasks.flatMap(t => {
                        const list: Task[] = [];
                        const collect = (item: Task) => {
                            if (item.type === 'task' && isTaskUnitAssigned(item.id, reg.physical_unit_id!, safeMatrixData, safeTasks)) {
                                list.push(item);
                            }
                            if (item.children) item.children.forEach(collect);
                        };
                        collect(t);
                        return list;
                    });

                    if (assignedTasks.length > 0) {
                        const isAnyProgrammed = currentActivePlan?.allocations?.some(
                            a => a.unitId === reg.physical_unit_id
                        );
                        const isAnyNotExecuted = weeklyPlans
                            .filter(p => p.status === 'Cerrada' && (!currentActivePlan || p.id !== currentActivePlan.id))
                            .some(p => p.allocations?.some(a => a.unitId === reg.physical_unit_id && a.executed === false));

                        const totalProgress = assignedTasks.reduce((sum, t) => sum + calculateUnitProgressLocal(t.id, reg.physical_unit_id!), 0);
                        const avgProgress = totalProgress / assignedTasks.length;

                        if (avgProgress >= 100) {
                            color = 'rgba(34, 197, 94, 0.65)'; // Completado
                        } else if (isAnyProgrammed) {
                            color = 'rgba(59, 130, 246, 0.75)'; // Programado
                        } else if (isAnyNotExecuted) {
                            color = 'rgba(239, 68, 68, 0.70)'; // No Ejecutado
                        } else if (avgProgress > 0) {
                            color = 'rgba(234, 179, 8, 0.70)'; // Avance Parcial
                        } else {
                            color = 'rgba(156, 163, 175, 0.45)'; // Sin Avance
                        }
                    }
                }
            }

            return {
                id: reg.id,
                pageNumber: reg.page_number || 1,
                xPercent: reg.x_percent || 0,
                yPercent: reg.y_percent || 0,
                widthPercent: reg.width_percent || 10,
                heightPercent: reg.height_percent || 10,
                label: shortLabel || reg.label,
                levelName: level?.name,
                unitName: unit?.name,
                color,
                physicalUnitId: reg.physical_unit_id
            };
        });
    }, [activePlanRegions, selectedTaskId, safeMatrixData, safeUnits, safeLevels, safeSavedProtocols, safeProtocolConfigs, projectInfo, weeklyPlans, activePlanId, safeTasks]);

    // Exportar PDF de la vista por tarea
    const handleExportPdf = async () => {
        if (!konvaViewerRef.current) return;
        setIsExportingPdf(true);
        try {
            const dataUrl = konvaViewerRef.current.toDataURL();
            if (!dataUrl) {
                alert("No se pudo generar la captura del plano.");
                return;
            }

            const dims = konvaViewerRef.current.getPdfDimensions();
            const isLandscape = dims.width >= dims.height;

            const doc = new jsPDF({
                orientation: isLandscape ? 'landscape' : 'portrait',
                unit: 'mm',
                format: 'a3'
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // Membrete
            doc.setFillColor(30, 41, 59);
            doc.rect(0, 0, pageWidth, 22, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text(`AVANCE GRÁFICO POR TAREA — ${projectInfo.name.toUpperCase()}`, 12, 11);

            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            const selectedTaskObj = selectedTaskId ? safeTasks.find(t => t.id === selectedTaskId) : null;
            const taskLabel = selectedTaskObj ? `Partida: ${selectedTaskObj.name}` : 'Vista General de Recintos';
            const planLabel = activePlan ? activePlan.nombre_visible : 'Plano';
            doc.text(`${taskLabel}  |  Plano: ${planLabel}`, 12, 17);

            const fechaStr = new Date().toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            doc.text(`Emitido: ${fechaStr}`, pageWidth - 12, 14, { align: 'right' });

            const margin = 10;
            const topMargin = 26;
            const availableWidth = pageWidth - (margin * 2);
            const availableHeight = pageHeight - topMargin - margin;

            const imgProps = doc.getImageProperties(dataUrl);
            const actualImgW = imgProps.width || dims.width || 1;
            const actualImgH = imgProps.height || dims.height || 1;
            const imgAspect = actualImgW / actualImgH;
            let imgW = availableWidth;
            let imgH = imgW / imgAspect;

            if (imgH > availableHeight) {
                imgH = availableHeight;
                imgW = imgH * imgAspect;
            }

            const imgX = margin + (availableWidth - imgW) / 2;
            const imgY = topMargin + (availableHeight - imgH) / 2;

            doc.setDrawColor(203, 213, 225);
            doc.rect(imgX - 1, imgY - 1, imgW + 2, imgH + 2, 'S');
            doc.addImage(dataUrl, 'PNG', imgX, imgY, imgW, imgH);

            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.text(`K-LyDApp / ConstructOS — Sistema de Gestión y Control de Producción`, margin, pageHeight - 4);

            doc.save(`Avance_Grafico_Tarea_${projectInfo.name.replace(/\s+/g, '_')}.pdf`);
        } catch (err) {
            console.error("Error exportando PDF:", err);
            alert("Error al generar PDF.");
        } finally {
            setIsExportingPdf(false);
        }
    };

    if (loading) {
        return <div className="p-8 flex justify-center"><span className="material-symbols-outlined animate-spin text-4xl text-blue-500">sync</span></div>;
    }

    if (plans.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-[550px] bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-8 text-center">
                <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600 mb-4">map</span>
                <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-2">Sin Planos Configurados</h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-md">
                    Este proyecto aún no tiene planos cargados. Dirígete a E.D.T y selecciona "Configuración de Avance Gráfico" para subir planos y mapear unidades físicas.
                </p>
                {onClose && (
                    <button onClick={onClose} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        Cerrar Visor
                    </button>
                )}
            </div>
        );
    }

    const selectedUnitObj = safeUnits.find(u => u.id === selectedUnitId);
    const selectedLevelObj = selectedUnitObj ? safeLevels.find(l => l.id === selectedUnitObj.levelId) : null;

    return (
        <div className={`flex flex-col h-full w-full bg-slate-100 dark:bg-slate-950 overflow-hidden relative select-none ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'rounded-xl'}`}>
            {/* Header Flotante */}
            <div className="absolute top-3 left-3 right-3 z-20 flex items-start justify-between pointer-events-none gap-3">
                {/* Selector de Planos */}
                <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-2 pointer-events-auto flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600 text-[20px]">map</span>
                        <span className="font-extrabold text-slate-800 dark:text-white text-xs uppercase tracking-wide">Plano:</span>
                    </div>
                    <select 
                        value={selectedPlanId} 
                        onChange={e => setSelectedPlanId(e.target.value)}
                        title={plans.find(p => p.id === selectedPlanId)?.nombre_visible}
                        className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-white outline-none font-medium focus:ring-1 focus:ring-blue-500"
                    >
                        {plans.map(p => {
                            const revMatch = p.nombre_original?.match(/\|rev:(\d+)$/);
                            const rev = revMatch ? ` (Rev. ${revMatch[1]})` : ' (Rev. 1)';
                            return (
                                <option key={p.id} value={p.id} title={p.nombre_visible}>
                                    {p.nombre_visible}{rev}
                                </option>
                            );
                        })}
                    </select>
                </div>

                {/* Leyenda y Botones de Acción */}
                <div className="flex items-center gap-2 pointer-events-auto">
                    {/* Leyenda Ejecutiva */}
                    <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-2 flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-semibold">
                            <div className="w-3.5 h-3.5 rounded-sm bg-[#22C55E] border border-green-600 shadow-sm"></div>
                            <span className="text-[11px]">Completado</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-semibold">
                            <div className="w-3.5 h-3.5 rounded-sm bg-[#3B82F6] border border-blue-600 shadow-sm"></div>
                            <span className="text-[11px]">Planificada</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-semibold">
                            <div className="w-3.5 h-3.5 rounded-sm bg-[#EAB308] border border-yellow-600 shadow-sm"></div>
                            <span className="text-[11px]">Avance Parcial</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-semibold">
                            <div className="w-3.5 h-3.5 rounded-sm bg-[#EF4444] border border-red-600 shadow-sm"></div>
                            <span className="text-[11px]">No Ejecutado</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300 font-semibold">
                            <div className="w-3.5 h-3.5 rounded-sm bg-[#9CA3AF] border border-slate-400 shadow-sm"></div>
                            <span className="text-[11px]">Sin Avance</span>
                        </div>
                    </div>

                    {/* Botones de Tamaño Completo y Descargar PDF */}
                    <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-1.5 flex items-center gap-2">
                        <button
                            onClick={handleExportPdf}
                            disabled={isExportingPdf || !activePlan}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                            title="Descargar plano en PDF"
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {isExportingPdf ? 'sync' : 'picture_as_pdf'}
                            </span>
                            <span>{isExportingPdf ? 'Generando...' : 'Descargar PDF'}</span>
                        </button>

                        <button
                            onClick={() => setIsFullscreen(prev => !prev)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all border border-slate-200 dark:border-slate-700 shadow-sm"
                            title={isFullscreen ? "Restaurar tamaño normal" : "Ampliar a pantalla completa"}
                        >
                            <span className="material-symbols-outlined text-[16px]">
                                {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                            </span>
                            <span>{isFullscreen ? 'Salir' : 'Agrandar Pantalla'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Visor Konva y Panel de Desglose */}
            <div className="flex-1 w-full h-full flex overflow-hidden relative">
                {/* Visor de Plano */}
                <div className="flex-1 w-full h-full">
                    {activePlan ? (
                        <PdfKonvaViewer 
                            ref={konvaViewerRef}
                            pdfUrl={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/graphic_plans/${activePlan.ruta_storage}`}
                            regions={konvaRegions}
                            interactive={false}
                            selectedRegionId={selectedUnitId}
                            onRegionClick={(reg: any) => {
                                if (reg?.physicalUnitId) {
                                    setSelectedUnitId(prev => prev === reg.physicalUnitId ? null : reg.physicalUnitId);
                                }
                            }}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full text-slate-400 font-medium">
                            Selecciona un plano para visualizar
                        </div>
                    )}
                </div>

                {/* Panel Lateral: Desglose de Tareas de la Unidad Seleccionada */}
                {selectedUnitId && selectedUnitObj && (
                    <div className="w-80 border-l border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-2xl flex flex-col z-30 animate-in slide-in-from-right duration-200">
                        {/* Cabecera del Desglose */}
                        <div className="p-3.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/80 dark:bg-slate-800/80">
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase font-black tracking-wider text-blue-600 dark:text-blue-400">
                                    Desglose de Partidas
                                </div>
                                <h4 className="font-extrabold text-sm text-slate-900 dark:text-white truncate">
                                    {selectedLevelObj ? `${selectedLevelObj.name} • ` : ''}{selectedUnitObj.name}
                                </h4>
                            </div>
                            <button 
                                onClick={() => setSelectedUnitId(null)}
                                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        {/* Lista de Partidas Asignadas */}
                        <div className="flex-1 overflow-y-auto p-3 space-y-2">
                            {unitTasksBreakdown.length === 0 ? (
                                <div className="text-center py-8 text-slate-400 text-xs italic">
                                    No hay tareas asociadas a esta unidad en la Matriz E.D.T.
                                </div>
                            ) : (
                                unitTasksBreakdown.map(({ task, progress, status }) => {
                                    let badgeBg = 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
                                    let badgeLabel = 'Sin Avance';
                                    let barColor = 'bg-slate-400';

                                    if (status === 'completed') {
                                        badgeBg = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300';
                                        badgeLabel = 'Completado';
                                        barColor = 'bg-emerald-500';
                                    } else if (status === 'programmed') {
                                        badgeBg = 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300';
                                        badgeLabel = 'Planificada';
                                        barColor = 'bg-blue-500';
                                    } else if (status === 'not_executed') {
                                        badgeBg = 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300';
                                        badgeLabel = 'No Ejecutado';
                                        barColor = 'bg-red-500';
                                    } else if (status === 'in_progress') {
                                        badgeBg = 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300';
                                        badgeLabel = 'En Proceso';
                                        barColor = 'bg-amber-500';
                                    }

                                    return (
                                        <div 
                                            key={task.id} 
                                            className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 shadow-sm flex flex-col gap-1.5"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <span className="text-xs font-bold text-slate-800 dark:text-slate-200 leading-tight">
                                                    {task.name}
                                                </span>
                                                <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded ${badgeBg} shrink-0`}>
                                                    {badgeLabel}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between text-[10px] text-slate-500 font-semibold">
                                                <span>Avance Acumulado:</span>
                                                <span className="font-extrabold text-slate-700 dark:text-slate-300">{Math.round(progress)}%</span>
                                            </div>
                                            <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                                                <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, progress)}%` }}></div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
